import { useState, useEffect } from "react";
import { GraduationCap, CheckCircle2, Clock, XCircle, Info, ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import AppLayout from "@/components/AppLayout";
import Footer from "@/components/Footer";
import { useI18n } from "@/context/I18nContext";
import Seo from "@/components/Seo";
import EducationFAQ from "@/components/EducationFAQ";

const TYPES = ["school", "university", "language_school", "training_center", "educational_nonprofit", "other_education"];
const ROLES = ["teacher", "school_admin", "principal", "director", "coordinator", "other"];
const ABOUT = ["what", "pilot", "who", "get", "privacy", "how"];

const STATUS_META = {
  pending_review: { icon: Clock, bg: "bg-butter" },
  needs_information: { icon: Info, bg: "bg-sky" },
  approved: { icon: CheckCircle2, bg: "bg-mint" },
  rejected: { icon: XCircle, bg: "bg-peach" },
};

const About = ({ t }) => (
  <div className="grid sm:grid-cols-2 gap-3 mt-6" data-testid="education-about">
    {ABOUT.map((k) => (
      <div key={k} className="nb-card p-4"><div className="font-cabinet font-bold">{t(`edu.about.${k}H`)}</div><p className="text-sm text-gray-600 mt-1">{t(`edu.about.${k}B`)}</p></div>
    ))}
  </div>
);

const FaqSection = ({ t, signedIn }) => (
  <section className="mt-10" data-testid="education-faq-section">
    <h2 className="font-cabinet font-extrabold text-2xl mb-3">{t("edu.faq.title")}</h2>
    {signedIn && <Link to="/join" className="text-sm underline font-bold inline-block mb-4" data-testid="education-join-code-link">{t("edu.stu.joinWithCode")}</Link>}
    <EducationFAQ />
  </section>
);

function PublicEducation() {
  const { t, dir } = useI18n();
  return (
    <div className="min-h-screen flex flex-col bg-paper" dir={dir}>
      <Seo title={`${t("education.title")} · ${t("edu.publicSub")}`} description={t("edu.publicSub")} />
      <header className="border-b-2 border-ink bg-white">
        <div className="max-w-3xl mx-auto px-4 h-16 flex items-center">
          <Link to="/" className="flex items-center gap-2 font-cabinet font-bold"><ArrowLeft className="w-5 h-5 rtl:rotate-180" /> <span>GetFluent<span className="text-coral">Loop</span></span></Link>
        </div>
      </header>
      <main className="flex-1 max-w-3xl mx-auto px-4 py-12" data-testid="education-public">
        <h1 className="font-cabinet font-extrabold text-4xl sm:text-5xl flex items-center gap-3"><GraduationCap className="w-10 h-10 shrink-0" /> {t("education.title")}</h1>
        <p className="text-gray-600 mt-3">{t("edu.publicSub")}</p>
        <About t={t} />
        <FaqSection t={t} />
        <div className="flex flex-wrap gap-3 mt-8">
          <Link to="/signup?next=/education" className="nb-btn-primary py-3 px-6" data-testid="education-public-signup">{t("edu.signupCta")}</Link>
          <Link to="/login?next=/education" className="nb-btn-outline py-3 px-6" data-testid="education-public-login">{t("edu.signinCta")}</Link>
        </div>
        <p className="text-xs text-gray-500 mt-3">{t("edu.returnHint")}</p>
      </main>
      <Footer />
    </div>
  );
}

export default function Education() {
  const { user, loading: authLoading } = useAuth();
  if (authLoading) return null;
  return user ? <EducationApply /> : <PublicEducation />;
}

function VerifyRespond({ existing, t, locale, onSent }) {
  const [f, setF] = useState({ institutional_email: "", staff_page_url: "", note: "" });
  const [busy, setBusy] = useState(false);
  const req = existing.verification_request || {};
  const hist = existing.verification_history || [];
  const waitingOwner = hist.length > 0 && hist[hist.length - 1].type === "response";
  const send = async () => {
    setBusy(true);
    try {
      await api.post(`/education/applications/${existing.application_id}/verification-response`, f);
      toast.success(t("edu.vSent"));
      setF({ institutional_email: "", staff_page_url: "", note: "" });
      onSent();
    } catch (e) { toast.error(e?.response?.data?.detail || t("edu.vFailed")); }
    finally { setBusy(false); }
  };
  return (
    <div className="nb-card bg-white p-4 mt-4 text-sm" data-testid="verification-request">
      <div className="font-bold">{t("edu.vTitle")}</div>
      {req.proofs?.length > 0 && (<><p className="mt-1">{t("edu.vIntro")}</p>
        <ul className="list-disc ms-5 mt-1">{req.proofs.map((p) => <li key={p}>{t(`edu.proofs.${p}`)}</li>)}</ul></>)}
      {req.message && <p className="mt-2 italic" dir="auto" data-testid="verification-request-message">{req.message}</p>}
      {waitingOwner ? <p className="mt-3 font-bold" data-testid="verification-received">{t("edu.vReceived")}</p> : (
        <div className="space-y-2 mt-3" data-testid="verification-respond-form">
          <Field label={t("edu.vEmail")}><input className="nb-input" dir="ltr" value={f.institutional_email} onChange={(e) => setF({ ...f, institutional_email: e.target.value })} data-testid="v-resp-email" /></Field>
          <p className="text-xs text-gray-500">{t("edu.vEmailHint")}</p>
          <Field label={t("edu.vUrl")}><input className="nb-input" dir="ltr" value={f.staff_page_url} onChange={(e) => setF({ ...f, staff_page_url: e.target.value })} placeholder="https://" data-testid="v-resp-url" /></Field>
          <Field label={t("edu.vNote")}><textarea className="nb-input" dir="auto" rows="2" maxLength={1000} value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} data-testid="v-resp-note" /></Field>
          <button disabled={busy || !(f.institutional_email.trim() || f.staff_page_url.trim() || f.note.trim())} onClick={send} className="nb-btn-primary py-2 px-4" data-testid="v-resp-send">{t("edu.vSend")}</button>
        </div>
      )}
      <ol className="mt-3 text-xs text-gray-600 space-y-0.5" data-testid="applicant-timeline">
        <li>{new Date(existing.submitted_at).toLocaleDateString(locale)} · {t("edu.ev.submitted")}</li>
        {hist.map((e, i) => <li key={i}>{new Date(e.at).toLocaleDateString(locale)} · {t(`edu.ev.${e.type}`)}</li>)}
      </ol>
    </div>
  );
}

function approvedMeta(a, t, locale) {
  const f = (d) => new Date(d).toLocaleDateString(locale, { year: "numeric", month: "long", day: "numeric" });
  const date = f(a.reviewed_at || a.submitted_at);
  if (!a.pilot?.ends_at) return t("edu.approvedOnly", { date });
  return t(a.pilot.active ? "edu.approvedActive" : "edu.approvedEnded", { date, end: f(a.pilot.ends_at) });
}

function StatusCard({ existing, t, locale, onSent }) {
  const meta = STATUS_META[existing.status] || STATUS_META.pending_review;
  return (
    <div className={`nb-card ${meta.bg} p-6 mt-6`} data-testid="application-status-card">
      <div className="flex items-center gap-2"><meta.icon className="w-6 h-6" />
        <span className="font-cabinet font-extrabold text-2xl" data-testid="application-status">{t(`edu.status.${existing.status}`)}</span>
      </div>
      <p className="text-sm mt-2"><bdi>{existing.institution_name}</bdi></p>
      {existing.status === "approved" && <p className="text-sm mt-1 font-bold">{t("edu.approvedLine")}</p>}
      {existing.status === "approved" && <Link to="/teacher" className="nb-btn-primary py-2 px-4 text-sm inline-flex mt-3" data-testid="application-approved-teacher-link">{t("edu.teacherLink")}</Link>}
      {existing.status === "needs_information" && existing.review_notes && (
        <p className="text-sm mt-2">{t("edu.reviewerNote", { note: existing.review_notes })}</p>
      )}
      {existing.status === "approved" ? (
        <p className="text-xs text-gray-600 mt-3" data-testid="application-approved-meta">{approvedMeta(existing, t, locale)}</p>
      ) : (
        <p className="text-xs text-gray-600 mt-3">{t("edu.submittedOn", { date: new Date(existing.submitted_at).toLocaleDateString(locale) })}</p>
      )}
      {existing.status !== "approved" && existing.verification_status === "needs_verification" && <VerifyRespond existing={existing} t={t} locale={locale} onSent={onSent} />}
    </div>
  );
}

function EducationApply() {
  const { t, locale } = useI18n();
  const [form, setForm] = useState({
    institution_name: "", institution_type: "school", country: "",
    institution_website: "", school_email: "", requested_role: "teacher",
    approx_student_count: "", notes: "", attestation: false,
  });
  const [existing, setExisting] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const load = () => api.get("/education/my-application").then((r) => setExisting((r.data.applications || [])[0] || null))
    .catch((e) => console.error("load application failed", e)).finally(() => setLoading(false));
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.institution_name.trim()) { toast.error(t("edu.toast.needName")); return; }
    if (!form.attestation) { toast.error(t("edu.attestNeeded")); return; }
    setSubmitting(true);
    try {
      const payload = { ...form, approx_student_count: form.approx_student_count ? parseInt(form.approx_student_count, 10) : null };
      const r = await api.post("/education/apply", payload);
      setExisting(r.data.application);
      toast.success(r.data.already_open ? t("edu.toast.alreadyOpen") : t("edu.toast.submitted"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("edu.toast.failed"));
    } finally { setSubmitting(false); }
  };

  const openStatus = existing && ["pending_review", "needs_information", "approved"].includes(existing.status);

  return (
    <AppLayout>
      <Seo title={t("education.title")} noindex />
      <div className="max-w-2xl mx-auto px-4 py-8">
        <div className="flex items-center gap-3">
          <span className="w-12 h-12 rounded-xl bg-mint border-2 border-ink flex items-center justify-center shrink-0"><GraduationCap className="w-6 h-6" /></span>
          <div>
            <h1 className="font-cabinet font-extrabold text-3xl">{t("education.title")}</h1>
            <p className="text-gray-600 text-sm">{t("education.pilotLine")}</p>
          </div>
        </div>

        {!loading && !(existing && openStatus) && (
          <>
            <p className="text-sm text-gray-600 mt-4" data-testid="education-intro-copy">{t("edu.intro")}</p>
            <About t={t} />
            <FaqSection t={t} signedIn />
          </>
        )}
        {loading ? (
          <div className="nb-card p-6 mt-6 animate-pulse h-40" />
        ) : existing && openStatus ? (
          <>
            <StatusCard existing={existing} t={t} locale={locale} onSent={load} />
            <FaqSection t={t} signedIn />
          </>
        ) : (
          <form onSubmit={submit} className="nb-card p-6 mt-6 space-y-4" data-testid="education-apply-form">
            {existing && existing.status === "rejected" && (
              <div className="nb-card bg-peach p-3 text-sm" data-testid="application-rejected-note">
                <div className="font-bold">{existing.applicant_message ? <bdi>{existing.applicant_message}</bdi> : t("edu.rejectedDefault")}</div>
                <div className="mt-1">{t("edu.resubmit")}</div>
              </div>
            )}
            <Field label={t("edu.f.name")}>
              <input className="nb-input" dir="auto" value={form.institution_name} onChange={(e) => set("institution_name", e.target.value)} data-testid="inst-name" placeholder="Lincoln High School" />
            </Field>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label={t("edu.f.type")}>
                <select className="nb-input" value={form.institution_type} onChange={(e) => set("institution_type", e.target.value)} data-testid="inst-type">
                  {TYPES.map((k) => <option key={k} value={k}>{t(`edu.types.${k}`)}</option>)}
                </select>
              </Field>
              <Field label={t("edu.f.country")}>
                <input className="nb-input" dir="auto" value={form.country} onChange={(e) => set("country", e.target.value)} data-testid="inst-country" />
              </Field>
            </div>
            <Field label={t("edu.f.website")}>
              <input className="nb-input" dir="ltr" value={form.institution_website} onChange={(e) => set("institution_website", e.target.value)} data-testid="inst-website" placeholder="https://lincolnhigh.edu" />
            </Field>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label={t("edu.f.email")}>
                <input className="nb-input" dir="ltr" value={form.school_email} onChange={(e) => set("school_email", e.target.value)} data-testid="inst-email" placeholder="you@lincolnhigh.edu" />
              </Field>
              <Field label={t("edu.f.role")}>
                <select className="nb-input" value={form.requested_role} onChange={(e) => set("requested_role", e.target.value)} data-testid="inst-role">
                  {ROLES.map((r) => <option key={r} value={r}>{t(`edu.roles.${r}`)}</option>)}
                </select>
              </Field>
            </div>
            <Field label={t("edu.f.learners")}>
              <input type="number" min="1" className="nb-input" value={form.approx_student_count} onChange={(e) => set("approx_student_count", e.target.value)} data-testid="inst-learners" placeholder="120" />
            </Field>
            <Field label={t("edu.f.notes")}>
              <textarea className="nb-input" dir="auto" rows="3" value={form.notes} onChange={(e) => set("notes", e.target.value)} data-testid="inst-notes" placeholder={t("edu.f.notesPh")} />
            </Field>
            <label className="flex items-start gap-2 text-sm" data-testid="attest-label">
              <input type="checkbox" className="mt-1" checked={form.attestation} onChange={(e) => set("attestation", e.target.checked)} data-testid="attest-checkbox" />
              <span>{t("edu.attest")}</span>
            </label>
            <p className="text-xs text-gray-500">{t("edu.f.disclaimer")}</p>
            <button type="submit" disabled={submitting} className="nb-btn-primary w-full py-3" data-testid="apply-submit-btn">
              {submitting ? t("education.submitting") : t("education.apply")}
            </button>
          </form>
        )}
      </div>
    </AppLayout>
  );
}

const Field = ({ label, children }) => (
  <div>
    <label className="text-xs font-bold uppercase tracking-widest rtl:tracking-normal text-gray-500">{label}</label>
    <div className="mt-1">{children}</div>
  </div>
);
